import { RefObject, useEffect, useReducer, useRef } from 'react';
import { LogsFilters } from 'data';
import { LogControllerService, LogLineDto } from 'data/openapi';
import { Constants, initialState, reducer, StateType } from './state';

// oldest
// ↑↑↑↑↑↑
//  logs
//  logs
//  logs
// ↓↓↓↓↓↓
// newest

export type UseLogsProps = {
  type: 'domain' | 'participant' | 'validator' | 'application';
  nodeName: string;
};

const loadingHandler = <T,>(
  setLoading: (v: boolean) => void,
  fetchFn: () => Promise<T>,
  successHandler: (data: T) => void,
  isFetchedRef: RefObject<boolean>,
  errorHandler: (error: any) => void,
) => {
  setLoading(true);
  fetchFn()
    .then(successHandler)
    .catch(errorHandler)
    .finally(() => {
      setLoading(false);
      // eslint-disable-next-line no-param-reassign
      if (!isFetchedRef.current) isFetchedRef.current = true;
    });
};

export const useLogs = (
  nodeName: UseLogsProps['nodeName'],
  filters: LogsFilters & { valueForTriggerFetch?: string | null },
) => {
  // TODO: should be tested
  const [state, dispatch] = useReducer<StateType, any>(reducer, initialState);
  const isFetchedRef = useRef(false);
  const { filter, level, numLogs, referenceTimeStamp, prevLogs, valueForTriggerFetch } = filters;

  const fetchData = (isDefaultFetch?: boolean) => {
    return LogControllerService.printLogs(
      nodeName,
      isDefaultFetch ? '' : referenceTimeStamp,
      isDefaultFetch ? false : prevLogs,
      numLogs,
      level,
      filter,
    );
  };

  const errorHandler = (err: any) => dispatch({ type: Constants.SET_ERROR, payload: err });
  const setLoading = (v: boolean) => dispatch({ type: Constants.SET_LOADING, payload: v });

  // fetch during change filters
  useEffect(() => {
    // for avoid double call to server
    if (isFetchedRef.current) {
      const successHandler = (data: LogLineDto[]) => dispatch({ type: Constants.SET_LOGS, payload: data });
      const fetchDataWithEmptyTimeStamp = () => fetchData(true);

      loadingHandler(setLoading, fetchDataWithEmptyTimeStamp, successHandler, isFetchedRef, errorHandler);
    }
  }, [filter, level]);

  // fetch during scroll
  useEffect(() => {
    const successScrollHandler = (data: LogLineDto[]) => {
      const payload = prevLogs ? [...data, ...state.logs] : [...state.logs, ...data];
      dispatch({ type: Constants.SET_LOGS, payload });
    };

    loadingHandler(setLoading, fetchData, successScrollHandler, isFetchedRef, errorHandler);
  }, [referenceTimeStamp, prevLogs, valueForTriggerFetch]);

  return {
    data: state.logs,
    isLogsLoading: state.isLoading,
    isFetched: isFetchedRef.current,
    errorMessage: state.errorMessage,
  };
};
